import React, { memo, useState } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';

import './index.scss';
import FavoriteMarker from '../FavoriteMarker';
import StoreHoursModal from '../StoreHoursModal';
import { Clock10 } from 'lucide-react';

const RestaurantCard = ({
  id,
  onFavoritePage,
  favorite,
  savedOrder,
  removeHandler,
  firstName,
  description,
  distance,
  profileImageUrl,
  shortAddress,
  slug,
  mainImageUrl,
  openRestaurantLocation,
  latitude,
  longitude,
  distanceUnit,
  availableForOrdering,
  presetTags,
  storeHours,
}) => {
  const [showStoreHoursModal, setShowStoreHoursModal] = useState(false);

  return (
    <div
      className={`made--meal meal-card slide slide--25 restaurant-card bg-white ${!availableForOrdering &&
        'restaurant-card-unavailableCover-hover'}`}
    >
      {favorite && !onFavoritePage && <FavoriteMarker />}
      {(favorite || savedOrder) && onFavoritePage && (
        <button
          type="button"
          className="close"
          style={{ zIndex: 1 }}
          onClick={e => {
            e.stopPropagation();
            removeHandler(id, favorite, savedOrder);
          }}
        >
          <svg className="icon icon-close-circle">
            <use xlinkHref="#spriteIcon-close-circle" />
          </svg>
        </button>
      )}

      <Link
        className="restaurant-card__top"
        to={!availableForOrdering ? '#' : slug || '/'}
      >
        <div
          className="restaurant-card__cover"
          style={{
            backgroundImage: `url(${mainImageUrl})`,
          }}
        >
          <div
            className="badges"
            style={{
              position: 'absolute',
              top: '4px',
              left: '4px',
            }}
          >
            {presetTags?.map(tag => (
              <div
                className="d-inline-block border-0"
                key={tag.id}
                style={{
                  backgroundColor: tag.color,
                  fontSize: '12px',
                  borderRadius: '20px',
                  margin: '3px 5px',
                  color: '#fff',
                  fontWeight: 'bold',
                  padding: '2px 12px',
                  boxShadow: 'rgb(0 0 0 / 10%) 0px 0px 5px 4px',
                }}
              >
                {tag.name}
              </div>
            ))}
          </div>
          <ul className="restaurant-card__logo">
            <li>
              <img
                className="img-fluid"
                src={profileImageUrl}
                alt={firstName}
              />
            </li>
          </ul>
          {!availableForOrdering ? (
            <div className="restaurant-card-unavailable__cover-hover">
              <button type="button" className="btn btn-sm btn-outline-primary">
                Unavailable
              </button>
            </div>
          ) : (
            <div className="restaurant-card__cover-hover">
              <button type="button" className="btn btn-sm btn-outline-primary">
                View Details
              </button>
            </div>
          )}
        </div>

        <div className="restaurant-card__info">
          <h4
            style={{ marginBottom: '3px' }}
            className="restaurant-card__info-name"
          >
            {firstName}
          </h4>
          {description && (
            <h4
              style={{ fontSize: 15, color: '#5e5e5e', fontWeight: 400 }}
              className="mb-0"
            >
              {description}
            </h4>
          )}
        </div>
      </Link>

      <a
        href="#map"
        className="restaurant-card__footer"
        onClick={e => {
          e.preventDefault();
          openRestaurantLocation({
            firstName,
            shortAddress,
            slug,
            latitude,
            longitude,
          });
        }}
      >
        <p className="restaurant-card__address">
          <img
            src="/assets/img/location.svg"
            className="restaurant-card__address-ico"
            alt="Location"
          />
          <span className="restaurant-card__address-p1">{shortAddress}</span>
          <span className="restaurant-card__address-p2">
            {distance} {distanceUnit} away
          </span>
          <span className="restaurant-card__footer-map">Map it</span>
        </p>
      </a>

      <a
        href="#store-hours"
        className="restaurant-card__footer-store-hours"
        onClick={(e) => {
          e.preventDefault();
          setShowStoreHoursModal(true);
        }}
      >
        <span>
          <Clock10 />
        </span>
      </a>
      <StoreHoursModal
        show={showStoreHoursModal}
        onClose={() => setShowStoreHoursModal(false)}
        firstName={firstName}
        shortAddress={shortAddress}
        storeHours={storeHours}
      />
    </div>
  );
};

RestaurantCard.propTypes = {
  id: PropTypes.number,
  onFavoritePage: PropTypes.bool,
  favorite: PropTypes.bool,
  savedOrder: PropTypes.bool,
  removeHandler: PropTypes.func,
  firstName: PropTypes.string,
  description: PropTypes.string,
  distance: PropTypes.number,
  profileImageUrl: PropTypes.string,
  shortAddress: PropTypes.string,
  slug: PropTypes.string,
  mainImageUrl: PropTypes.string,
  openRestaurantLocation: PropTypes.func,
  latitude: PropTypes.string,
  longitude: PropTypes.string,
  distanceUnit: PropTypes.string,
  storeHours: PropTypes.string,
};

RestaurantCard.defaultProps = {
  id: 0,
  onFavoritePage: false,
  favorite: false,
  savedOrder: false,
  removeHandler: () => {},
  firstName: '',
  description: '',
  distance: 0,
  profileImageUrl: '',
  shortAddress: '',
  slug: '',
  latitude: '',
  longitude: '',
  distanceUnit: '',
  mainImageUrl: '/assets/img/restaurant-card-placeholder.jpg',
  openRestaurantLocation: () => {},
  storeHours: '',
};

export default memo(RestaurantCard);
